/**
 * The one rule about an `<a>` whose destination is not this portal.
 *
 * `client/calls.html`'s *Join Meet* is `<a href="https://meet.google.com/…">` with
 * no `target` and no `rel`, so clicking it **replaced the portal** — a client on
 * their way into a call left the page they were reading, and coming back is the
 * browser's Back button and a full reload. It is not the only external link in the
 * SPA and it will not be the last, so this is a rule rather than a fix: every
 * primitive that renders an anchor asks this function, and no page passes a prop.
 *
 * That is the whole reason it is a module and not a `target` prop on `Linkish`. A
 * prop is a decision at the call site, and the call site is a page — pages compose
 * primitives and state facts; whether `meet_url` happens to be off-origin is not a
 * fact about the call, it is a fact about the URL, and the URL is here.
 *
 * **External means "another origin over http(s)", and nothing else.**
 * `mailto:hello@…` and `tel:` hand off to another application without navigating,
 * so a `_blank` on one leaves a blank tab behind and is the annoyance people are
 * right to complain about. `#invoices`, `/app/billing` and `/api/…/invoice.pdf` are
 * all this origin: an in-app anchor, a route, and a download the browser keeps on
 * the page. Only a genuine cross-origin document navigation gets the new tab.
 *
 * `rel="noopener noreferrer"` comes with it and is not optional. `noopener` is the
 * security half — without it the opened document can reach back through
 * `window.opener` — and `noreferrer` keeps the portal's own path, which carries
 * refs, out of somebody else's logs.
 */

/** Exactly what gets spread onto the anchor. Empty for an href that stays here. */
export type ExternalLinkProps =
  Record<string, never> | { readonly target: '_blank'; readonly rel: 'noopener noreferrer' }

const SAME_TAB: Record<string, never> = {}
const NEW_TAB = { target: '_blank', rel: 'noopener noreferrer' } as const

export function externalLinkProps(href: string | undefined): ExternalLinkProps {
  return isExternalHref(href) ? NEW_TAB : SAME_TAB
}

/**
 * Whether following this href leaves the portal's origin in the same document.
 *
 * Asked of the URL parser rather than of the string, for the reason
 * `routes/login.tsx` `safeRedirect` gives at length: `/\evil.example` and
 * `/<tab>/evil.example` are both `//evil.example` by the time a browser has read
 * them, and a `startsWith('/')` test says "internal" about both. Here that mistake
 * is only a missing `target` rather than an open redirect, but it is the same
 * mistake and there is no reason to make it twice.
 */
export function isExternalHref(href: string | undefined): boolean {
  if (href === undefined || href === '') return false

  let resolved: URL
  try {
    resolved = new URL(href, window.location.href)
  } catch {
    // Not a URL at all — nothing will navigate anywhere, and a `_blank` on it
    // would open an empty tab.
    return false
  }

  if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return false
  return resolved.origin !== window.location.origin
}
